import { useEffect, useRef, useState } from "react";
import type { EntryConditions } from "../types";
import { formatAgo, formatPrice, isStale, parseDbTimestamp } from "../lib/format";
import { Card } from "./ui/card";
import { cn } from "@/lib/utils";

interface Props {
  symbol: string;
  data: EntryConditions | null;
  lastUpdatedAt: number | null;
  now: number;
  error: Error | null;
}

export function PriceHeader({ symbol, data, lastUpdatedAt, now, error }: Props) {
  const prevPrice = useRef<number | null>(null);
  const [tick, setTick] = useState<"up" | "down" | null>(null);

  useEffect(() => {
    if (!data) return;
    const prev = prevPrice.current;
    if (prev !== null && prev !== data.current_price) {
      setTick(data.current_price > prev ? "up" : "down");
      const id = setTimeout(() => setTick(null), 400);
      prevPrice.current = data.current_price;
      return () => clearTimeout(id);
    }
    prevPrice.current = data.current_price;
  }, [data]);

  const stale = isStale(lastUpdatedAt, 2500, now);
  const serverAge = data ? now - parseDbTimestamp(data.cre_on) : null;

  return (
    <Card className="flex items-center justify-between gap-4 px-4 py-2.5">
      <div className="flex items-baseline gap-3">
        <span className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
          {symbol}
        </span>
        <span
          className={cn(
            "font-mono-tabular text-2xl font-semibold price-tick leading-none",
            tick === "up" && "text-buy",
            tick === "down" && "text-sell"
          )}
        >
          {data ? `$${formatPrice(data.current_price)}` : "—"}
        </span>
      </div>
      <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
        <span
          className={cn(
            "inline-block h-2.5 w-2.5 rounded-full",
            stale
              ? "bg-warn"
              : "bg-buy shadow-[0_0_6px_hsl(var(--buy))] status-pulse"
          )}
          aria-hidden="true"
        />
        <span>
          {error ? (
            <span className="text-sell">{error.message}</span>
          ) : (
            <>
              Client: {formatAgo(lastUpdatedAt, now)}
              {serverAge !== null && (
                <>
                  {" · "}
                  Server:{" "}
                  <span className={serverAge > 3000 ? "text-warn" : ""}>
                    {Math.round(serverAge / 100) / 10}s ago
                  </span>
                </>
              )}
            </>
          )}
        </span>
      </div>
    </Card>
  );
}
